import { addAlertThunk } from 'actions/alerts/AlertsActions';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { createOnlineBookingWaitlistThunk } from 'actions/onlineBooking/actions/OnlineBookingWaitlistActions';
import { WaitlistError, WaitlistErrorMessages } from 'actions/waitlists/WaitlistsErrors';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { Input } from 'components/Form/Input/Input';
import { Label } from 'components/Form/Label/Label';
import { Select } from 'components/Form/Select/Select';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import { useTimeZone } from 'hooks/useTimeZone';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import {
    getOnlineBookingWaitlistCategory,
    getOnlineBookingWaitlistItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { dispatch } from 'store';
import { Option } from 'types/Generics';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getMarketplaceBusinessHoursForDate } from 'utils/marketplaceScheduleUtils';
import {
    applyWaitlistItemDates,
    getWaitlistItemDates,
    getWaitlistTimeOptions,
    parseWaitlistTimeId,
    WAITLIST_DATE_FORMAT
} from 'utils/waitlistUtils';
import { WaitlistDateField } from './PickerField/WaitlistDateField';
import { WaitlistDatesField } from './PickerField/WaitlistDatesField';
import { WaitlistTimeField } from './PickerField/WaitlistTimeField';
import classes from './OnlineBookingJoinWaitlistStep.module.scss';

type RequestedDateMode = 'asap' | 'specific';
type PreferredTimeMode = 'anytime' | 'specific';
type ValidUntilMode = 'ongoing' | 'specific';

const REQUESTED_DATE_OPTIONS: Array<Option<RequestedDateMode>> = [
    { id: 'asap', name: 'ASAP' },
    { id: 'specific', name: 'Specific Date' }
];

const PREFERRED_TIME_OPTIONS: Array<Option<PreferredTimeMode>> = [
    { id: 'anytime', name: 'Anytime' },
    { id: 'specific', name: 'Specific Hour' }
];

const VALID_UNTIL_OPTIONS: Array<Option<ValidUntilMode>> = [
    { id: 'ongoing', name: 'Ongoing' },
    { id: 'specific', name: 'Specific Date' }
];

export const OnlineBookingJoinWaitlistStep: React.FC = () => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();
    const { isPending } = useOnlineBookingState();
    const waitlistItems = useSelector(getOnlineBookingWaitlistItems);
    const waitlistCategory = useSelector(getOnlineBookingWaitlistCategory);
    const olbDateLimits = useSelector(getOlbDateLimits);

    // Every pet of the request shares the same dates: the ones picked in the cart for this
    // business area, which this step can still change.
    const requestedDates = React.useMemo(() => {
        const item = waitlistItems.find(item => item.service);

        return item ? getWaitlistItemDates(item) : [];
    }, [waitlistItems]);

    const firstRequestedDate =
        requestedDates.length > 0 ? moment(requestedDates[0], WAITLIST_DATE_FORMAT) : null;

    // Always opens on ASAP; the cart dates are preloaded if the client switches to a specific date.
    const [requestedDateMode, setRequestedDateMode] = React.useState<RequestedDateMode>('asap');
    const [preferredTimeMode, setPreferredTimeMode] = React.useState<PreferredTimeMode>('anytime');
    const [preferredTime, setPreferredTime] = React.useState<string | null>(null);
    const [validUntilMode, setValidUntilMode] = React.useState<ValidUntilMode>('ongoing');
    const [validUntil, setValidUntil] = React.useState<Moment | null>(null);
    const [notes, setNotes] = React.useState('');

    // Today in the location time zone, as a plain calendar date for the pickers.
    const today = React.useMemo(
        () => moment(moment().tz(timeZone).format('YYYY-MM-DD'), 'YYYY-MM-DD'),
        [timeZone]
    );

    const isAsap = requestedDateMode === 'asap';

    // The preferred time and "valid until" follow the first requested date
    const effectiveDate = isAsap ? null : firstRequestedDate;

    const validUntilMinDate =
        effectiveDate && effectiveDate.isAfter(today, 'day') ? effectiveDate : today;

    // The same booking window the date step of each business area offers.
    const limitPreferences = marketplace.onlineBookingPreferences.bookingDateLimits;

    const minDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.from?.find(limit => limit.id === limitPreferences.from)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.from]
    );

    const maxDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.to?.find(limit => limit.id === limitPreferences.to)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.to]
    );

    // A waitlist request asks for a day without availability, so only the days the
    // location is closed are out.
    const isDateDisabled = React.useCallback(
        (calendarDate: Date) =>
            getMarketplaceBusinessHoursForDate(
                marketplace,
                moment(calendarDate).tz(timeZone, true)
            ) === null,
        [marketplace, timeZone]
    );

    // Asked for every day of the calendar, not just the disabled ones, so an open day
    // (or one only out of the booking window) has to answer with nothing.
    const getDisabledDateTooltip = React.useCallback(
        (calendarDate: Date) => {
            const date = moment(calendarDate).tz(timeZone, true);

            if (getMarketplaceBusinessHoursForDate(marketplace, date) !== null) {
                return undefined;
            }

            return `Marketplace closed on ${date.format('MMM D, YYYY')}`;
        },
        [marketplace, timeZone]
    );

    const timeOptions = React.useMemo(
        () => getWaitlistTimeOptions(marketplace, effectiveDate),
        [marketplace, effectiveDate]
    );

    const hasServices = waitlistItems.some(item => item.service);

    const isValid =
        hasServices &&
        (isAsap || requestedDates.length > 0) &&
        (preferredTimeMode === 'anytime' || !!preferredTime) &&
        (validUntilMode === 'ongoing' || !!validUntil);

    // Changing the requested dates can leave the preferred time outside that day's hours
    // or the "valid until" date before the first requested date.
    const applyRequestedDate = (date: Moment | null) => {
        const nextTimeOptions = getWaitlistTimeOptions(marketplace, date);

        if (preferredTime && !nextTimeOptions.some(timeOption => timeOption.id === preferredTime)) {
            setPreferredTime(null);
        }

        if (date && validUntil && validUntil.isBefore(date, 'day')) {
            setValidUntil(null);
        }
    };

    const requestedDateModeChangeHandler = (option: Option<RequestedDateMode>) => {
        setRequestedDateMode(option.id);
        applyRequestedDate(option.id === 'specific' ? firstRequestedDate : null);
    };

    // Every pet of the request goes on the same dates, as they do in the cart.
    const requestedDatesChangeHandler = (dates: Array<string>) => {
        dispatch(
            updateOnlineBookingBookingItems(
                waitlistItems.map(item => applyWaitlistItemDates(item, dates, timeZone))
            )
        );

        applyRequestedDate(dates.length > 0 ? moment(dates[0], WAITLIST_DATE_FORMAT) : null);
    };

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    const submitHandler = async () => {
        if (!isValid || isPending) {
            return;
        }

        try {
            await dispatch(
                createOnlineBookingWaitlistThunk({
                    asap: isAsap,
                    preferredTime:
                        preferredTimeMode === 'specific' && preferredTime
                            ? parseWaitlistTimeId(preferredTime)
                            : null,
                    validUntil: validUntilMode === 'specific' ? validUntil : null,
                    notes: notes.trim()
                })
            );

            dispatch(navigateToStep(OnlineBookingStep.WaitlistSuccess));
        } catch (error) {
            dispatch(
                addAlertThunk(
                    error instanceof WaitlistError ? error.message : WaitlistErrorMessages.Unknown
                )
            );
        }
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full mb-6">Join Waitlist</h1>

                <ErrorList className="mb-6" />

                <div className={classes.fields}>
                    <div className={classes.row}>
                        <div className={classes.field}>
                            <Label className={classes.label}>Requested Date</Label>

                            <Select
                                value={requestedDateMode}
                                options={REQUESTED_DATE_OPTIONS}
                                onChange={requestedDateModeChangeHandler}
                            />
                        </div>

                        {!isAsap && (
                            <WaitlistDatesField
                                label="Select Date"
                                businessArea={waitlistCategory}
                                dates={requestedDates}
                                timeZone={timeZone}
                                minDate={minDate}
                                maxDate={maxDate}
                                isDateDisabled={isDateDisabled}
                                getDisabledDateTooltip={getDisabledDateTooltip}
                                onChange={requestedDatesChangeHandler}
                            />
                        )}
                    </div>

                    <div className={classes.row}>
                        <div className={classes.field}>
                            <Label className={classes.label}>Preferred Time</Label>

                            <Select
                                value={preferredTimeMode}
                                options={PREFERRED_TIME_OPTIONS}
                                onChange={option => setPreferredTimeMode(option.id)}
                            />
                        </div>

                        {preferredTimeMode === 'specific' && (
                            <WaitlistTimeField
                                label="Select Hour"
                                value={preferredTime}
                                options={timeOptions}
                                onChange={setPreferredTime}
                            />
                        )}
                    </div>

                    <div className={classes.row}>
                        <div className={classes.field}>
                            <Label className={classes.label}>Valid Until</Label>

                            <Select
                                value={validUntilMode}
                                options={VALID_UNTIL_OPTIONS}
                                onChange={option => setValidUntilMode(option.id)}
                            />
                        </div>

                        {validUntilMode === 'specific' && (
                            <WaitlistDateField
                                label="Select Date"
                                value={validUntil}
                                minDate={validUntilMinDate}
                                onChange={setValidUntil}
                            />
                        )}
                    </div>

                    <div>
                        <Label className={classes.label}>Notes</Label>

                        <Input
                            multiline
                            value={notes}
                            placeholder="Notes here..."
                            onChange={e => setNotes(e.target.value)}
                        />
                    </div>
                </div>

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Join Waitlist"
                        loading={isPending}
                        disabled={!isValid || isPending}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <OnlineBookingResume waitlist waitlistAsap={isAsap} />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
